import React from "react";
import {
  FetchOptions,
  isInTrialMode,
  isSchoolUser,
  isUserExpired,
  isUserSignedUpAndConsented,
  nonGuestProfiles,
  Profile,
  ProfileState,
  User,
} from "../api";
import { LiveData } from "../utils";

export const MAX_PROFILES_PER_USER = 8;

// Adding a profile when locked sends you to upgrade instead.
export const isProfileManagementUnlocked = (user: User) => {
  return isUserSignedUpAndConsented(user) && !isUserExpired(user);
};

export const addProfilePath = (user: User) =>
  isProfileManagementUnlocked(user)
    ? "/account/profile/add/"
    : "/account/upgrade/";

export const editProfilePath = (profileId: number) =>
  `/account/profile/edit/${profileId}/`;

export class Account {
  constructor(
    public user: User,
    public userCurrentVersion: number,
    public userExpectedVersion: number,
    public profile: Profile | null,
    public profileStateCurrentVersion: number,
    public profileStateExpectedVersion: number,
    public profileState: ProfileState | null
  ) {}

  public copy({
    user,
    userCurrentVersion,
    userExpectedVersion,
    profile,
    profileStateCurrentVersion,
    profileStateExpectedVersion,
    profileState,
  }: {
    user?: User;
    userCurrentVersion?: number;
    userExpectedVersion?: number;
    profile?: Profile | null;
    profileStateCurrentVersion?: number;
    profileStateExpectedVersion?: number;
    profileState?: ProfileState | null;
  }) {
    return new Account(
      user === undefined ? this.user : user,
      userCurrentVersion === undefined
        ? this.userCurrentVersion
        : userCurrentVersion,
      userExpectedVersion === undefined
        ? this.userExpectedVersion
        : userExpectedVersion,
      profile === undefined ? this.profile : profile,
      profileStateCurrentVersion === undefined
        ? this.profileStateCurrentVersion
        : profileStateCurrentVersion,
      profileStateExpectedVersion === undefined
        ? this.profileStateExpectedVersion
        : profileStateExpectedVersion,
      profileState === undefined ? this.profileState : profileState
    );
  }

  public hasUserAndMaybeProfile() {
    return this.userCurrentVersion === this.userExpectedVersion;
  }

  public canCreateProfile() {
    return (
      !isInTrialMode(this.user) &&
      !isSchoolUser(this.user) &&
      nonGuestProfiles(this.user).length < MAX_PROFILES_PER_USER
    );
  }

  public hasMaybeProfileState() {
    return (
      this.hasUserAndMaybeProfile() &&
      this.profileStateCurrentVersion === this.profileStateExpectedVersion
    );
  }

  // The profile whose progress the Profile tab shows. An expired account is
  // switched to its guest profile, so fall back to the newest real profile.
  public displayProfile() {
    if (this.profile && !this.profile.is_guest) {
      return this.profile;
    }
    const profiles = nonGuestProfiles(this.user);
    return profiles[profiles.length - 1] ?? null;
  }

  public hasNonGuestProfile() {
    return this.profile?.is_guest === false;
  }

  public rosterName() {
    return (
      (this.profile?.roster_first_name || "") +
      " " +
      (this.profile?.roster_last_name || "")
    ).trim();
  }
}

export const enum RetryBehavior {
  RETRY_ALL,
  RETRY_ALL_CANCELLABLE,
  HANDLE_40X_RETRY_OTHER_CANCELLABLE,
  RETRY_EXCEPTION_EXPOSE_NON_SUCCESS,
  EXPOSE_ALL,
}

export type MakeRequestResponse = {
  code: number;
  body?: any;
  isSuccessful: boolean;
  errorBody?: any;
  headers: Response["headers"];
  blob: () => Promise<Blob>;
  text: () => Promise<string>;
};

export interface AccountContextValue {
  makeRequest: (
    url: string,
    method: string,
    retry: RetryBehavior,
    sendJwt: boolean,
    body?: any,
    fetchOptions?: FetchOptions
  ) => Promise<MakeRequestResponse>;
  accountLiveData: LiveData<Account>;
  login: (access_token: string, user: User) => void;
  loginWithClever: (options?: {
    next?: string | null;
    ssoRetry?: boolean;
  }) => void;
  loginWithClassLink: (options?: {
    next?: string | null;
    ssoRetry?: boolean;
  }) => void;
  logout: () => void;
  updateUser: (
    user: User,
    skipRefreshingProfileState: boolean,
    switchToProfileId: number | null,
    clearProfile: boolean
  ) => void;
  updateProfile: (profile: Profile) => void;
  addProfile: (profile: Profile) => void;
  removeProfile: (profile: Profile) => void;
  forceReloadUserAndProfile: () => void;
  forceReloadProfileState: () => void;
  agreeToTermsOfUse: () => void;
}

export const AccountContext = React.createContext<AccountContextValue>(null!);
